import React, {useState, useEffect, useRef, useCallback, useMemo} from "react";
import {
    CreditCard, Loader, Box, Clock, ArrowDown, ArrowUp,
    Receipt, Clipboard, X, Lock, ArrowLeft,
    Wallet, ChevronDown,
} from "lucide-react";
import {useTranslation} from "react-i18next";
import secureStorage from "../../utils/secureStorage";
import {api} from "../../config/api";
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import moment from "moment";
import {fundMovementCategoryLabel} from "./fundMovementCategories";
import {tabClass, tabIconClass} from '../Common/TabBar';
import RegisterSelect from "./components/RegisterSelect";
import Button from "../Common/Button";
import {Group, Row} from "./components/GroupedList";
import {thClass} from "../../stock/StockUI";

// Fund-movement `note` is stored as a JSON blob by the POS terminal:
// {"type":"sortie","motif":"...","category":"...","isFundsMovement":true,"timestamp":...}
// Parse it into a clean { motif, category, type } shape, falling back to the raw
// string (older/unexpected formats) so nothing ever renders blank.
const parseFundMovementNote = (note) => {
    if (!note) return {motif: "—", category: null, type: null, cancelled: false};
    try {
        const parsed = JSON.parse(note);
        if (parsed && typeof parsed === "object") {
            return {
                motif: parsed.motif || "—",
                category: parsed.category || null,
                type: parsed.type || null,
                cancelled: !!parsed.cancelled,
            };
        }
    } catch {
        // Not JSON — treat the whole string as the motif.
    }
    return {motif: note, category: null, type: null, cancelled: false};
};

// iOS system colours for the payment share bar (same as the POS).
const SEGMENT_COLORS = ['#34C759', '#007AFF', '#FF9500', '#AF52DE', '#5AC8FA', '#FF2D55', '#FFCC00', '#8E8E93'];

// Colonne dédiée (API) en priorité, note JSON pour les mouvements
// synchronisés avant son ajout.
const isFundMovementCancelled = (mvt) =>
    mvt.movementCancelled ?? parseFundMovementNote(mvt.note).cancelled;

// Moment's locale is synced globally by useSyncMomentLocale() at the app root.

const JournauxTables = ({_dateRange = [], _startTime, _endTime, refreshKey = 0, isDarkMode = false}) => {
    const {t} = useTranslation("activite");
    const calendarRef            = useRef(null);
    const scrollContainerRef     = useRef(null);
    const loadingRef             = useRef(null);
    const [selectedJournal, setSelectedJournal]   = useState(null);
    // Days folded in the history pane ("YYYY-MM-DD")
    const [collapsedDays, setCollapsedDays]       = useState(() => new Set());
    const toggleDay = (day) => setCollapsedDays((current) => {
        const next = new Set(current);
        next.has(day) ? next.delete(day) : next.add(day);
        return next;
    });
    const [showCalendar, setShowCalendar]         = useState(false);
    const [activeTab, setActiveTab]               = useState("products");
    const [currentPage, setCurrentPage]           = useState(1);
    // eslint-disable-next-line no-unused-vars
    const [totalPages, setTotalPages]             = useState(1);
    // eslint-disable-next-line no-unused-vars
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [allJournals, setAllJournals]           = useState([]);
    // Filtre par caisse ('' = toutes) — voir components/RegisterSelect.js
    const [register, setRegister]                 = useState('');
    const [hasMore, setHasMore]                   = useState(true);
    const [isLoadingMore, setIsLoadingMore]       = useState(false);
    const [showCloseModal, setShowCloseModal]     = useState(false);
    const [closingAmount, setClosingAmount]       = useState('');
    const [collapsedFundDates, setCollapsedFundDates] = useState({});
    const queryClient = useQueryClient();

    const toggleFundDateGroup = (dateKey) => {
        setCollapsedFundDates(prev => ({...prev, [dateKey]: !prev[dateKey]}));
    };

    const dateRangeKey = useMemo(() => {
        if (!_dateRange || _dateRange.length === 0) return '';
        const range = _dateRange[0];
        const startDate = range?.startDate ? moment(range.startDate).format('YYYY-MM-DD') : '';
        const endDate   = range?.endDate   ? moment(range.endDate).format('YYYY-MM-DD')   : '';
        return `${startDate}-${endDate}-${_startTime||''}-${_endTime||''}`;
    }, [_dateRange, _startTime, _endTime]);

    useEffect(() => {
        setCurrentPage(1); setAllJournals([]); setHasMore(true);
    }, [debouncedSearchTerm]);

    useEffect(() => {
        setCurrentPage(1); setAllJournals([]); setHasMore(true);
    }, [dateRangeKey]);

    useEffect(() => {
        setCurrentPage(1); setAllJournals([]); setHasMore(true);
    }, [register]);

    useEffect(() => {
        setCurrentPage(1); setAllJournals([]); setHasMore(true);
    }, [refreshKey]);

    const fetchJournals = async (page = 1) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            let url = `/journal?page=${page}`;
            if (debouncedSearchTerm.trim()) url += `&id=${encodeURIComponent(debouncedSearchTerm.trim())}`;
            if (_dateRange?.length > 0) {
                const dr = _dateRange[0];
                if (dr.startDate) url += `&startDate=${encodeURIComponent(moment(dr.startDate).format('YYYY-MM-DD'))}`;
                if (dr.endDate)   url += `&endDate=${encodeURIComponent(moment(dr.endDate).format('YYYY-MM-DD'))}`;
            }
            if (_startTime) url += `&startTime=${encodeURIComponent(_startTime)}`;
            if (_endTime)   url += `&endTime=${encodeURIComponent(_endTime)}`;
            if (register)   url += `&registers=${encodeURIComponent(register)}`;

            const response = await api.get(url, { headers: { 'X-account-id': accountId.id, 'Accept': 'application/json' } });
            if (response.data.pagination) {
                setTotalPages(response.data.pagination.totalPages);
                return { data: response.data.data, pagination: response.data.pagination };
            }
            return { data: response.data, pagination: null };
        } catch (error) {
            throw new Error(error.response?.data?.message || t("journaux.fetchError"));
        }
    };

    const {data: journalsData, isLoading, error} = useQuery({
        queryKey: ['journals', currentPage, debouncedSearchTerm, dateRangeKey, refreshKey, register],
        queryFn: () => fetchJournals(currentPage),
        keepPreviousData: false,
    });

    const {data: journalDetails, isLoading: isLoadingDetails, error: detailsError, refetch: refetchDetails} = useQuery({
        queryKey: ['journalDetails', selectedJournal?.id],
        queryFn: async () => {
            if (!selectedJournal?.id) return null;
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get(`/journal/details/${selectedJournal.id}`, {
                headers: { 'X-account-id': accountId.id, 'Accept': 'application/json' }
            });
            return response.data;
        },
        enabled: !!selectedJournal?.id
    });

    const closeJournalMutation = useMutation({
        mutationFn: async ({journalId, endTotal}) => {
            const accountId  = await secureStorage.getItem("selectedAccount");
            const currentUser = await secureStorage.getItem("user");
            const formData = new FormData();
            formData.append('is_open', 'false');
            formData.append('end_total', parseFloat(endTotal).toString());
            formData.append('end_date', Math.floor(Date.now() / 1000).toString());
            formData.append('updated_at', Math.floor(Date.now() / 1000).toString());
            if (currentUser?.employee?.id)
                formData.append('end_employee_id', `/api/employee/${currentUser.employee.id}`);
            const response = await api.put(`/journal/${journalId}`, formData, {
                headers: { 'X-account-id': accountId.id, 'Accept': 'application/json', 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['journals']);
            queryClient.invalidateQueries(['journalDetails', selectedJournal?.id]);
            setShowCloseModal(false); setClosingAmount(''); setSelectedJournal(null);
        },
        onError: (error) => {
            alert(error.response?.data?.message || t("journaux.closeError"));
        }
    });

    const handleCloseJournal = () => {
        if (!closingAmount || parseFloat(closingAmount) < 0) { alert(t("journaux.invalidAmount")); return; }
        closeJournalMutation.mutate({ journalId: selectedJournal.id, endTotal: closingAmount });
    };

    const loadMore = useCallback(() => {
        if (!isLoadingMore && hasMore && !isLoading) {
            setIsLoadingMore(true); setCurrentPage(prev => prev + 1);
        }
    }, [isLoadingMore, hasMore, isLoading]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => { if (entries[0].isIntersecting) loadMore(); },
            { root: scrollContainerRef.current, rootMargin: '100px', threshold: 0.1 }
        );
        const el = loadingRef.current;
        if (el) observer.observe(el);
        return () => { if (el) observer.unobserve(el); };
    }, [loadMore]);

    useEffect(() => {
        if (journalsData) {
            if (currentPage === 1) {
                setAllJournals(journalsData.data || []);
            } else {
                setAllJournals(prev => {
                    const existingIds = new Set(prev.map(j => j.id));
                    return [...prev, ...(journalsData.data || []).filter(j => !existingIds.has(j.id))];
                });
            }
            setHasMore(journalsData.pagination ? currentPage < journalsData.pagination.totalPages : false);
            setIsLoadingMore(false);
        }
    }, [journalsData, currentPage]);

    useEffect(() => {
        const handler = (e) => { if (calendarRef.current && !calendarRef.current.contains(e.target)) setShowCalendar(false); };
        if (showCalendar) document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showCalendar]);

    // Ticks every minute while an open journal's details are shown, so the
    // "ouvert depuis" duration stays live without needing a network refetch.
    const [durationTick, setDurationTick] = useState(0);
    useEffect(() => {
        if (!journalDetails?.journal?.isOpen) return;
        const id = setInterval(() => setDurationTick(t => t + 1), 60000);
        return () => clearInterval(id);
    }, [journalDetails?.journal?.isOpen]);

    const formatPrice    = (p) => (!p ? "-" : p.toFixed(2));
    const formatCurrency = (v) => (v == null ? "0.00" : parseFloat(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " "));


    // "Ouvert depuis" — days/hours/minutes since opening, for still-open journals.
    const openDuration = useMemo(() => {
        const j = journalDetails?.journal;
        if (!j?.isOpen || !j.startDate) return null;
        const dur = moment.duration(moment().diff(moment(j.startDate)));
        const days = Math.floor(dur.asDays());
        const hours = dur.hours();
        const minutes = dur.minutes();
        const parts = [];
        if (days > 0) parts.push(t("journaux.durationDay", {count: days}));
        if (hours > 0 || days > 0) parts.push(t("journaux.durationHour", {count: hours}));
        parts.push(t("journaux.durationMinute", {count: minutes}));
        return parts.join(' ');
        // `durationTick` is intentionally a dep with no direct use — it's what
        // forces this to recompute every minute while the journal stays open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [journalDetails?.journal?.isOpen, journalDetails?.journal?.startDate, durationTick, t]);

    // Group fund movements by calendar day (most recent day first), each day
    // keeping the API's own createdAt DESC order.

    const fundMovementGroups = useMemo(() => {
        const movements = journalDetails?.fundMovements || [];
        const byDate = {};
        movements.forEach((mvt) => {
            const dateKey = mvt.createdAt ? moment(mvt.createdAt).format("YYYY-MM-DD") : "unknown";
            if (!byDate[dateKey]) byDate[dateKey] = [];
            byDate[dateKey].push(mvt);
        });
        return Object.keys(byDate)
            .sort((a, b) => b.localeCompare(a))
            .map((dateKey) => ({
                dateKey,
                items: byDate[dateKey],
                // Un mouvement annulé reste listé (traçabilité) mais ne compte
                // pas — même règle que le POS et les totaux de l'API.
                total: byDate[dateKey].reduce((s, m) => (isFundMovementCancelled(m) ? s : s + (m.total || 0)), 0),
            }));
    }, [journalDetails?.fundMovements]);

    // Écart de caisse — only meaningful once the journal is closed: compares the
    // declared closing amount to what the till should hold (opening float + the
    // "Total des paiements" — all payment methods, same figure as the summary
    // card below, not cash alone).
    const closingEcart = useMemo(() => {
        const j = journalDetails?.journal;
        if (!j || j.isOpen || j.endTotal === null || j.endTotal === undefined) return null;

        const totalPayments = (journalDetails.payments || [])
            .reduce((sum, p) => sum + (parseFloat(p.totalAmount) || 0), 0);

        const expected = (parseFloat(j.startTotal) || 0) + totalPayments;
        const actual = parseFloat(j.endTotal) || 0;
        const diff = actual - expected;

        return {
            totalPayments,
            expected,
            actual,
            diff,
            hasDifference: Math.abs(diff) >= 0.01,
        };
    }, [journalDetails]);
    const pageBgClass = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
    const panelClass = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
    const textPrimaryClass = isDarkMode ? 'text-gray-100' : 'text-gray-800';
    const textSecondaryClass = isDarkMode ? 'text-gray-300' : 'text-gray-700';
    const textMutedClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
    const subtleBorderClass = isDarkMode ? 'border-gray-700' : 'border-gray-200';
    const rowBorderClass = isDarkMode ? 'border-gray-700' : 'border-gray-100';

    // ── Loading ───────────────────────────────────────────────────────────────
    if (isLoading && currentPage === 1) return (
        <div className="flex justify-center items-center h-40">
            <Loader className="w-8 h-8 text-primary animate-spin"/>
        </div>
    );

    if (error && allJournals.length === 0) return (
        <div className="p-4 text-center text-red-500">{t("journaux.errorPrefix", {message: error.message})}</div>
    );

    // ── Detail content (shared between mobile full-screen & desktop pane) ─────
    // Apple style, same layout as the POS journal detail (JournalDetailView):
    // large title, hero amount + stats strip, then grouped lists — edge to edge
    // on phones, rounded cards from md up.
    const salesTotal = journalDetails?.sales?.reduce((s, v) => s + (v.total || 0), 0) || 0;
    const ticketsCount = journalDetails?.sales?.length || 0;
    const paymentsTotal = journalDetails?.payments?.reduce((s, p) => s + (parseFloat(p.totalAmount) || 0), 0) || 0;
    const fmtDate = (d) => d ? moment(d).format("ddd D MMM · HH:mm") : "—";
    const ecartLabel = closingEcart && (closingEcart.hasDifference
        ? `${closingEcart.diff > 0 ? '+' : ''}${formatCurrency(closingEcart.diff)} DH`
        : `✓ ${t("journaux.noDifference")}`);
    const ecartColor = closingEcart && (closingEcart.hasDifference ? 'text-red-500' : 'text-emerald-500');

    const DetailContent = () => (
        <div className="pb-10">
            {isLoadingDetails ? (
                <div className="flex justify-center items-center h-48">
                    <Loader className="w-8 h-8 text-primary animate-spin"/>
                </div>
            ) : detailsError ? (
                <div className="m-4 text-center p-6 rounded-lg border bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800 space-y-3">
                    <p className="text-red-600">{t("journaux.detailsLoadError")}</p>
                    {/* The API reason (status + message) so a failing journal can be diagnosed */}
                    <p className="text-xs text-red-500/80 break-words">
                        {[detailsError.response?.status,
                          detailsError.response?.data?.detail || detailsError.response?.data?.message || detailsError.message]
                            .filter(Boolean).join(" · ")}
                    </p>
                    <div className="flex justify-center gap-2">
                        <Button variant="secondary" size="sm" icon={ArrowLeft} className="md:hidden" onClick={() => setSelectedJournal(null)}>
                            {t("journaux.back")}
                        </Button>
                        <Button variant="tinted" size="sm" onClick={() => refetchDetails()}>{t("journaux.retry")}</Button>
                    </div>
                </div>
            ) : journalDetails ? (
                <div className="w-full">
                    {/* Large title */}
                    <div className="flex items-start gap-2 px-4 pt-4">
                        <div className="flex-1 min-w-0">
                            <Button variant="plain" size="sm" icon={ArrowLeft} className="md:hidden -ml-2 mb-1"
                                    onClick={() => setSelectedJournal(null)}>
                                {t("journaux.back")}
                            </Button>
                            <h2 className="text-[28px] leading-tight font-bold tracking-tight text-gray-900 dark:text-gray-100 truncate">
                                {t("journaux.journalNumber", {id: journalDetails.journal.id.slice(0, 8)})}
                            </h2>
                            <p className="mt-0.5 text-[15px] text-gray-500 dark:text-gray-400 truncate">
                                {[
                                    journalDetails.journal.registerName,
                                    journalDetails.journal.isOpen ? t("journaux.open") : t("journaux.closed"),
                                    openDuration && t("journaux.openSince", {duration: openDuration}),
                                ].filter(Boolean).join(" · ")}
                            </p>
                        </div>
                        {journalDetails.journal.isOpen && (
                            <Button variant="danger" size="sm" icon={Lock} onClick={() => setShowCloseModal(true)}>
                                <span className="hidden sm:inline">{t("journaux.closeJournal")}</span>
                                <span className="sm:hidden">{t("journaux.close")}</span>
                            </Button>
                        )}
                        <Button variant="ghost" size="sm" icon={X} title={t("journaux.close")} className="hidden md:inline-flex"
                                onClick={() => setSelectedJournal(null)}/>
                    </div>

                    {/* Hero */}
                    <div className="flex flex-col items-center pt-5 md:px-4">
                        <p className="text-4xl sm:text-[40px] font-bold tracking-tight tabular-nums text-gray-900 dark:text-gray-100">
                            {formatCurrency(salesTotal)} DH
                        </p>
                        <p className="text-[15px] text-gray-500 dark:text-gray-400 mt-0.5">{t("journaux.totalSales")}</p>
                        <div className="self-stretch mt-5 grid grid-cols-3 py-3 bg-white dark:bg-gray-800 border-y md:border md:rounded-xl border-gray-200 dark:border-gray-700 divide-x divide-gray-200 dark:divide-gray-700">
                            {[
                                {label: t("journaux.detail.tickets"), value: ticketsCount},
                                {label: t("journaux.detail.averageBasket"), value: `${formatCurrency(ticketsCount ? salesTotal / ticketsCount : 0)} DH`},
                                {label: t("journaux.cashDifference"), value: closingEcart ? ecartLabel : "—", className: ecartColor},
                            ].map((st) => (
                                <div key={st.label} className="flex flex-col items-center px-2 min-w-0">
                                    <span className={`text-[17px] font-semibold tabular-nums truncate max-w-full ${st.className || 'text-gray-900 dark:text-gray-100'}`}>{st.value}</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-full">{st.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Période */}
                    <Group title={t("journaux.detail.periodTitle")}>
                        <Row first label={t("journaux.openDate")} value={fmtDate(journalDetails.journal.startDate)}/>
                        <Row label={t("journaux.closeDate")}
                             value={journalDetails.journal.endDate ? fmtDate(journalDetails.journal.endDate) : t("journaux.inProgress")}
                             valueClass={!journalDetails.journal.endDate ? 'text-primary' : undefined}/>
                        {journalDetails.journal.startEmployee?.name && <Row label={t("journaux.openedBy")} value={journalDetails.journal.startEmployee.name}/>}
                        {journalDetails.journal.endEmployee?.name && <Row label={t("journaux.closedBy")} value={journalDetails.journal.endEmployee.name}/>}
                    </Group>

                    {/* Paiements — share bar + one row per method */}
                    <Group title={t("journaux.detail.paymentsTitle")}>
                        {journalDetails.payments?.length > 0 ? (
                            <>
                                <div className="pr-4 pt-3.5 pb-1">
                                    <div className="flex h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700">
                                        {journalDetails.payments.map((pm, i) => {
                                            const share = paymentsTotal > 0 ? Math.max(0, parseFloat(pm.totalAmount) || 0) / paymentsTotal : 0;
                                            return share > 0 && <div key={i} style={{flex: share, backgroundColor: SEGMENT_COLORS[i % SEGMENT_COLORS.length]}}/>;
                                        })}
                                    </div>
                                </div>
                                {journalDetails.payments.map((pm, i) => {
                                    const amount = parseFloat(pm.totalAmount) || 0;
                                    const pct = paymentsTotal > 0 ? Math.round(Math.max(0, amount) / paymentsTotal * 100) : 0;
                                    return (
                                        <Row key={i} first={i === 0}
                                             leading={<span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{backgroundColor: SEGMENT_COLORS[i % SEGMENT_COLORS.length]}}/>}
                                             label={`${pm.paymentName} (${pm.paymentCount})`}
                                             value={`${formatCurrency(amount)} DH · ${pct} %`}/>
                                    );
                                })}
                                <Row strong label={t("journaux.totalPayments")} value={`${formatCurrency(paymentsTotal)} DH`}/>
                            </>
                        ) : (
                            <Row first label={t("journaux.detail.noPayment")}/>
                        )}
                    </Group>

                    {/* Fond de caisse */}
                    <Group title={t("journaux.detail.cashTitle")}>
                        <Row first label={t("journaux.openingBalance")} value={`${formatCurrency(journalDetails.journal.startTotal)} DH`}/>
                        <Row label={t("journaux.totalPayments")} value={`+ ${formatCurrency(paymentsTotal)} DH`}/>
                        <Row strong label={t("journaux.detail.expected")}
                             value={`${formatCurrency((parseFloat(journalDetails.journal.startTotal) || 0) + paymentsTotal)} DH`}/>
                        {closingEcart && (
                            <>
                                <Row label={t("journaux.detail.counted")} value={`${formatCurrency(closingEcart.actual)} DH`}/>
                                <Row strong label={t("journaux.cashDifference")} value={ecartLabel} valueClass={ecartColor}/>
                            </>
                        )}
                    </Group>

                    {/* Détails — products, payments, sales… */}
                    <h3 className="mt-7 mb-1.5 px-4 md:px-8 text-[13px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {t("journaux.detail.detailsTitle")}
                    </h3>
                    {/* Tabs */}
                    <div className={`md:mx-4 border-y md:border md:rounded-xl overflow-hidden ${panelClass}`}>
                        <div className={`border-b px-2 sm:px-4 ${subtleBorderClass}`}>
                            <nav className="flex overflow-x-auto scrollbar-hide -mb-px space-x-1 sm:space-x-2 md:justify-center" style={{scrollbarWidth:'none'}}>
                                {[
                                    {id:"products",   label:t("journaux.tabs.products"),    Icon:Box},
                                    {id:"categories", label:t("journaux.tabs.categories"),  Icon:Box},
                                    {id:"payments",   label:t("journaux.tabs.payments"),   Icon:CreditCard},
                                    {id:"sales",      label:t("journaux.tabs.sales"),      Icon:Receipt},
                                    {id:"sale_canceld",      label:t("journaux.tabs.cancelledSales"),      Icon:Receipt},
                                    {id:"fund_movements",      label:t("journaux.tabs.fundMovements"),      Icon:Wallet},
                                ].map(({id, label, Icon}) => (
                                    <button key={id} onClick={() => setActiveTab(id)}
                                        className={tabClass(activeTab === id)}
                                    >
                                        <Icon className={tabIconClass}/>{label}
                                    </button>
                                ))}
                            </nav>
                        </div>
                        <div className="overflow-x-auto">
                            {activeTab === "products" && (
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                                        <tr>
                                            {[
                                                t("journaux.productsColumns.product"),
                                                t("journaux.productsColumns.quantity"),
                                                t("journaux.productsColumns.revenue"),
                                                t("journaux.productsColumns.averagePrice"),
                                                t("journaux.productsColumns.sales"),
                                            ].map((h,i) => (
                                                <th key={i} className={`${thClass} whitespace-nowrap ${i===0?"":"!text-right"}`}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {journalDetails.products?.map((p, i) => (
                                            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className={`px-6 py-4 text-sm font-semibold whitespace-nowrap text-gray-900 dark:text-gray-100`}>{p.productName}</td>
                                                <td className={`px-6 py-4 text-sm text-right ${textSecondaryClass}`}>{p.totalQuantity}</td>
                                                <td className={`px-6 py-4 text-sm text-right font-semibold ${textSecondaryClass}`}>{formatCurrency(p.totalRevenue)} DH</td>
                                                <td className={`px-6 py-4 text-sm text-right ${textSecondaryClass}`}>{formatCurrency(p.averagePrice)} DH</td>
                                                <td className={`px-6 py-4 text-sm text-right ${textSecondaryClass}`}>{p.salesCount}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {activeTab === "categories" && (
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                                        <tr>
                                            {[
                                                t("journaux.categoriesColumns.category"),
                                                t("journaux.categoriesColumns.product"),
                                                t("journaux.categoriesColumns.units"),
                                                t("journaux.categoriesColumns.revenue"),
                                                t("journaux.categoriesColumns.transactions"),
                                            ].map((h,i) => (
                                                <th key={i} className={`${thClass} whitespace-nowrap ${i<=1?"":"!text-right"}`}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {journalDetails.productsCategory?.map((c, i) => (
                                            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className={`px-6 py-4 text-sm font-semibold whitespace-nowrap text-gray-900 dark:text-gray-100`}>{c.categoryName || "—"}</td>
                                                <td className={`px-6 py-4 text-sm whitespace-nowrap ${textSecondaryClass}`}>{c.productName}</td>
                                                <td className={`px-6 py-4 text-sm text-right ${textSecondaryClass}`}>{c.totalQuantity}</td>
                                                <td className={`px-6 py-4 text-sm text-right font-semibold ${textSecondaryClass}`}>{formatCurrency(c.totalRevenue)} DH</td>
                                                <td className={`px-6 py-4 text-sm text-right ${textSecondaryClass}`}>{c.salesCount}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {activeTab === "payments" && (
                                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                    {journalDetails.payments?.map((payment, i) => (
                                        <div key={i} className={`rounded-lg p-4 border ${isDarkMode ? 'bg-gray-900/70 border-gray-700' : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'}`}>
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className={`font-semibold text-sm ${textPrimaryClass}`}>{payment.paymentName}</h4>
                                                <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-xs font-medium">{payment.paymentCount}</span>
                                            </div>
                                            <p className={`text-xl sm:text-2xl font-bold ${textSecondaryClass}`}>{formatCurrency(payment.totalAmount)} DH</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {activeTab === "sales" && (
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                                        <tr>
                                            {[
                                                t("journaux.salesColumns.id"),
                                                t("journaux.salesColumns.status"),
                                                t("journaux.salesColumns.totalTtc"),
                                                t("journaux.salesColumns.totalHt"),
                                                t("journaux.salesColumns.discount"),
                                                t("journaux.salesColumns.device"),
                                            ].map((h,i) => (
                                                <th key={i} className={`${thClass} whitespace-nowrap ${[0,1,5].includes(i)?"":"!text-right"}`}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {journalDetails.sales?.map((sale, i) => (
                                            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className={`px-6 py-4 text-sm font-semibold whitespace-nowrap text-gray-900 dark:text-gray-100`}>#{sale.id?.slice(0,8)}</td>
                                                <td className="px-6 py-4 text-sm whitespace-nowrap">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                        sale.status==='completed'
                                                            ? (isDarkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-100 text-emerald-700')
                                                            : sale.status==='pending'
                                                                ? (isDarkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700')
                                                                : (isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700')
                                                    }`}>{sale.status}</span>
                                                </td>
                                                <td className={`px-6 py-4 text-sm text-right font-semibold whitespace-nowrap ${textSecondaryClass}`}>{formatCurrency(sale.total)} DH</td>
                                                <td className={`px-6 py-4 text-sm text-right whitespace-nowrap ${textSecondaryClass}`}>{formatCurrency(sale.subTotal)} DH</td>
                                                <td className={`px-6 py-4 text-sm text-right whitespace-nowrap ${textSecondaryClass}`}>{formatCurrency(sale.discount)} DH</td>
                                                <td className={`px-6 py-4 text-sm whitespace-nowrap ${textSecondaryClass}`}>{sale.deviceName || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            {activeTab === "sale_canceld" && (
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                                        <tr>
                                            {[
                                                t("journaux.salesColumns.id"),
                                                t("journaux.salesColumns.status"),
                                                t("journaux.salesColumns.totalTtc"),
                                                t("journaux.salesColumns.totalHt"),
                                                t("journaux.salesColumns.discount"),
                                                t("journaux.salesColumns.device"),
                                            ].map((h,i) => (
                                                <th key={i} className={`${thClass} whitespace-nowrap ${[0,1,5].includes(i)?"":"!text-right"}`}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {journalDetails.sale_canceld?.map((sale, i) => (
                                            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className={`px-6 py-4 text-sm font-semibold whitespace-nowrap text-gray-900 dark:text-gray-100`}>#{sale.id?.slice(0,8)}</td>
                                                <td className="px-6 py-4 text-sm whitespace-nowrap">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                        sale.status==='completed'
                                                            ? (isDarkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-100 text-emerald-700')
                                                            : sale.status==='pending'
                                                                ? (isDarkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-700')
                                                                : (isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700')
                                                    }`}>{sale.status}</span>
                                                </td>
                                                <td className={`px-6 py-4 text-sm text-right font-semibold whitespace-nowrap ${textSecondaryClass}`}>{formatCurrency(sale.total)} DH</td>
                                                <td className={`px-6 py-4 text-sm text-right whitespace-nowrap ${textSecondaryClass}`}>{formatCurrency(sale.subTotal)} DH</td>
                                                <td className={`px-6 py-4 text-sm text-right whitespace-nowrap ${textSecondaryClass}`}>{formatCurrency(sale.discount)} DH</td>
                                                <td className={`px-6 py-4 text-sm whitespace-nowrap ${textSecondaryClass}`}>{sale.deviceName || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {activeTab === "fund_movements" && (
                                fundMovementGroups.length > 0 ? (
                                    <div className="p-4 space-y-3">
                                        {fundMovementGroups.map(({dateKey, items, total}) => {
                                            const isCollapsed = !!collapsedFundDates[dateKey];
                                            const dayIsIn = total >= 0;
                                            return (
                                                <div key={dateKey} className={`rounded-xl border overflow-hidden ${panelClass}`}>
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleFundDateGroup(dateKey)}
                                                        className={`w-full flex items-center justify-between gap-3 px-3 sm:px-4 py-3 text-left transition-colors ${isDarkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}`}
                                                    >
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${textMutedClass} ${isCollapsed ? '-rotate-90' : ''}`}/>
                                                            <span className={`font-semibold text-xs sm:text-sm truncate ${textPrimaryClass}`}>
                                                                {dateKey !== "unknown" ? moment(dateKey).format("dddd DD MMMM YYYY") : t("journaux.unknownDate")}
                                                            </span>
                                                            <span className={`hidden sm:inline text-xs flex-shrink-0 ${textMutedClass}`}>({t("journaux.movementsCount", {count: items.length})})</span>
                                                        </div>
                                                        <span className={`text-xs sm:text-sm font-bold flex-shrink-0 whitespace-nowrap ${dayIsIn ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-700') : (isDarkMode ? 'text-red-400' : 'text-red-700')}`}>
                                                            {dayIsIn ? "+" : "−"}{formatCurrency(Math.abs(total))} DH
                                                        </span>
                                                    </button>

                                                    {!isCollapsed && (
                                                        <div className={`border-t divide-y ${subtleBorderClass} ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                                            {items.map((mvt, i) => {
                                                                const parsedNote = parseFundMovementNote(mvt.note);
                                                                const {motif} = parsedNote;
                                                                const category = mvt.movementCategory || parsedNote.category;
                                                                const isCancelled = isFundMovementCancelled(mvt);
                                                                const isIn = (mvt.total || 0) >= 0;
                                                                return (
                                                                    <div key={i}
                                                                         className={`flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 px-3 sm:px-4 py-2.5 text-xs sm:text-sm transition-colors ${isDarkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}`}>
                                                                        <span className={`sm:w-16 flex-shrink-0 whitespace-nowrap ${textSecondaryClass}`}>
                                                                            {mvt.createdAt ? moment(mvt.createdAt).format("HH:mm") : "—"}
                                                                        </span>
                                                                        <span className="sm:w-24 flex-shrink-0">
                                                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                                isIn
                                                                                    ? (isDarkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-100 text-emerald-700')
                                                                                    : (isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700')
                                                                            }`}>
                                                                                {isIn ? <ArrowUp className="w-3 h-3"/> : <ArrowDown className="w-3 h-3"/>}
                                                                                {isIn ? t("journaux.cashIn") : t("journaux.cashOut")}
                                                                            </span>
                                                                        </span>
                                                                        <span className={`sm:w-28 flex-shrink-0 sm:text-right font-semibold ${isCancelled ? 'line-through opacity-60' : ''} ${isIn ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-700') : (isDarkMode ? 'text-red-400' : 'text-red-700')}`}>
                                                                            {isIn ? "+" : "−"}{formatCurrency(Math.abs(mvt.total || 0))} DH
                                                                        </span>
                                                                        <span className={`flex-1 min-w-0 ${textSecondaryClass}`}>
                                                                            {motif}
                                                                            {category && (
                                                                                <span className={`ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                                                                    {fundMovementCategoryLabel(t, category)}
                                                                                </span>
                                                                            )}
                                                                            {isCancelled && (
                                                                                <span className={`ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'}`}>
                                                                                    {t("fundMovementCategories.cancelled")}
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className={`text-center py-8 ${textMutedClass}`}>
                                        <Wallet className="w-8 h-8 mx-auto mb-2 opacity-50"/>
                                        {t("journaux.noFundMovements")}
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className={`text-center p-6 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <p className={textMutedClass}>{t("journaux.noDetailsAvailable")}</p>
                </div>
            )}
        </div>
    );

    return (
        <div className={`text-sm min-h-screen ${pageBgClass}`}>
            {/* Close modal */}
            {showCloseModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
                    <div className={`rounded-xl shadow-2xl p-6 w-full max-w-md border ${panelClass}`}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className={`text-lg sm:text-xl font-bold flex items-center gap-2 ${textPrimaryClass}`}>
                                <Lock className="w-5 h-5 text-primary"/>{t("journaux.closeJournal")}
                            </h3>
                            <button onClick={() => { setShowCloseModal(false); setClosingAmount(''); }} className={`${textMutedClass} ${isDarkMode ? 'hover:text-gray-200' : 'hover:text-gray-600'}`}>
                                <X className="w-5 h-5"/>
                            </button>
                        </div>
                        <div className="mb-5">
                            <label className={`block text-sm font-medium mb-2 ${textSecondaryClass}`}>{t("journaux.closingFund")}</label>
                            <input type="number" step="0.01" min="0" value={closingAmount}
                                onChange={(e) => setClosingAmount(e.target.value)}
                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${isDarkMode ? 'bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
                                placeholder="0.00" autoFocus
                            />
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => { setShowCloseModal(false); setClosingAmount(''); }}
                                className={`flex-1 px-4 py-2 border rounded-lg transition-colors text-sm ${isDarkMode ? 'border-gray-700 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                {t("journaux.cancel")}
                            </button>
                            <button onClick={handleCloseJournal} disabled={closeJournalMutation.isLoading}
                                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm">
                                {closeJournalMutation.isLoading ? <><Loader className="w-4 h-4 animate-spin"/>{t("journaux.closing")}</> : t("journaux.closeJournal")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="w-full">
                {selectedJournal === null ? (
                    /* ── Journal list ── */
                    <div ref={scrollContainerRef} className="max-h-screen overflow-y-auto scrollbar-hide" style={{scrollbarWidth:'none'}}>
                        {/* No top border: the select's own border is the top line; -mb-px lays its
                            bottom border over the row's hairline instead of doubling it */}
                        <div className={`border-b ${panelClass}`}>
                            <div className="flex border-b border-gray-200 dark:border-gray-700"><RegisterSelect value={register} onChange={setRegister} className="w-full sm:w-72 -mb-px"/></div>
                            {/* Desktop table */}
                            <div className="hidden sm:block">
                                <table className="w-full border-collapse">
                                    <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700">
                                        <tr className={`text-left ${textSecondaryClass}`}>
                                            <th className="pl-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{t("journaux.openingColumn")}</th>
                                            <th className="py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{t("registers.column")}</th>
                                            <th className="py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{t("journaux.closingColumn")}</th>
                                            <th className="py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{t("journaux.openingFund")}</th>
                                            <th className="py-4 pr-6 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{t("journaux.closingFundColumn")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allJournals.length === 0 && !isLoading ? (
                                            <tr><td colSpan="5" className={`text-center py-12 ${textMutedClass}`}>
                                                <Clipboard className="w-12 h-12 mx-auto mb-3 opacity-30"/>
                                                <p className="text-sm">{t("journaux.noJournalsFound")}</p>
                                            </td></tr>
                                        ) : allJournals.map((journal) => (
                                            <tr key={journal.id}
                                                className={`border-b cursor-pointer transition-colors duration-150 ${rowBorderClass} ${isDarkMode ? 'hover:bg-primary/10' : 'hover:bg-primary/5'}`}
                                                onClick={() => setSelectedJournal(journal)}
                                            >
                                                <td className={`pl-6 py-4 text-sm ${textSecondaryClass}`}>
                                                    {journal.startDate ? moment(journal.startDate).format("DD MMM YYYY HH:mm") : "—"}
                                                </td>
                                                <td className={`py-4 text-sm ${journal.registerName ? 'font-medium text-primary' : textMutedClass}`}>
                                                    {journal.registerName || "—"}
                                                </td>
                                                <td className="py-4 text-sm">
                                                    {journal.endDate ? (
                                                        <span className={textSecondaryClass}>{moment(journal.endDate).format("DD MMM YYYY HH:mm")}</span>
                                                    ) : (
                                                        <span className={`font-medium px-3 py-1 rounded-full text-xs ${isDarkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-600'}`}>{t("journaux.inProgress")}</span>
                                                    )}
                                                </td>
                                                <td className={`py-4 text-sm ${textSecondaryClass}`}>{journal.startTotal ? `${formatPrice(journal.startTotal)} DH` : "—"}</td>
                                                <td className={`py-4 pr-6 text-right font-medium text-sm ${textSecondaryClass}`}>{journal.endTotal ? `${formatPrice(journal.endTotal)} DH` : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile card list */}
                            <div className={`sm:hidden divide-y ${rowBorderClass}`}>
                                {allJournals.length === 0 && !isLoading ? (
                                    <div className={`text-center py-12 ${textMutedClass}`}>
                                        <Clipboard className="w-10 h-10 mx-auto mb-3 opacity-30"/>
                                        <p className="text-sm">Aucun journal trouvé</p>
                                    </div>
                                ) : allJournals.map((journal) => (
                                    <div key={journal.id}
                                        className={`p-4 cursor-pointer transition-colors duration-150 ${isDarkMode ? 'hover:bg-primary/10' : 'hover:bg-primary/5'}`}
                                        onClick={() => setSelectedJournal(journal)}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <p className={`text-xs mb-0.5 ${textMutedClass}`}>{t("journaux.openingColumn")}</p>
                                                <p className={`font-medium text-sm ${textPrimaryClass}`}>
                                                    {journal.startDate ? moment(journal.startDate).format("DD MMM YYYY HH:mm") : "—"}
                                                </p>
                                            </div>
                                            {journal.endDate ? (
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'text-gray-300 bg-gray-700' : 'text-gray-500 bg-gray-100'}`}>{t("journaux.closed")}</span>
                                            ) : (
                                                <span className={`font-medium px-2 py-0.5 rounded-full text-xs ${isDarkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-600'}`}>{t("journaux.inProgress")}</span>
                                            )}
                                        </div>
                                        <div className={`grid grid-cols-2 gap-2 text-xs ${textMutedClass}`}>
                                            <div>
                                                <p className="mb-0.5">{t("journaux.openingFund")}</p>
                                                <p className={`font-semibold ${textPrimaryClass}`}>{journal.startTotal ? `${formatPrice(journal.startTotal)} DH` : "—"}</p>
                                            </div>
                                            <div>
                                                <p className="mb-0.5">{t("journaux.closingFundColumn")}</p>
                                                <p className={`font-semibold ${textPrimaryClass}`}>{journal.endTotal ? `${formatPrice(journal.endTotal)} DH` : "—"}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {hasMore && (
                                <div ref={loadingRef} className="flex justify-center items-center p-6">
                                    {isLoadingMore && <Loader className="w-6 h-6 text-primary animate-spin"/>}
                                </div>
                            )}
                            {!hasMore && allJournals.length > 0 && (
                                <div className={`text-center p-4 text-xs ${textMutedClass}`}>{t("journaux.endOfResults")}</div>
                            )}
                        </div>
                    </div>
                ) : (
                    /* ── Detail view ── */
                    <>
                        {/* Mobile: full-screen */}
                        <div className={`md:hidden min-h-screen overflow-y-auto scrollbar-hide ${pageBgClass}`} style={{scrollbarWidth:'none'}}>
                            <DetailContent/>
                        </div>

                        {/* Desktop: side-by-side */}
                        <div className={`hidden md:flex h-screen ${pageBgClass}`}>
                            {/* Sidebar */}
                            <div className={`w-1/3 xl:w-1/4 border-r overflow-y-auto scrollbar-hide ${panelClass}`} style={{scrollbarWidth:'none'}}>
                                {/* Pane title — same bar as the Paramètres side menu */}
                                <div className={`h-14 px-4 flex items-center justify-between gap-2 border-b sticky top-0 z-20 ${subtleBorderClass} ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
                                    <h3 className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${textSecondaryClass}`}>
                                        <Clock className="w-4 h-4 text-primary"/>{t("journaux.journalsHistory")}
                                    </h3>
                                    <span className={`text-xs ${textMutedClass}`}>{allJournals.length}</span>
                                </div>
                                {allJournals.map((journal, index) => {
                                    const day = journal.startDate ? moment(journal.startDate).format("YYYY-MM-DD") : "";
                                    const previousDay = index > 0 && allJournals[index - 1].startDate
                                        ? moment(allJournals[index - 1].startDate).format("YYYY-MM-DD") : null;
                                    const isActive = selectedJournal.id === journal.id;
                                    const isOpen = !journal.endDate;
                                    const isFolded = collapsedDays.has(day);
                                    const dayCount = day !== previousDay
                                        ? allJournals.filter((j) => j.startDate && moment(j.startDate).format("YYYY-MM-DD") === day).length
                                        : 0;
                                    return (
                                        <React.Fragment key={journal.id}>
                                            {/* Day header, once per day */}
                                            {day !== previousDay && (
                                                <button
                                                    type="button"
                                                    onClick={() => toggleDay(day)}
                                                    aria-expanded={!isFolded}
                                                    className={`sticky top-14 z-10 w-full flex items-center gap-2 px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider border-b transition-colors ${subtleBorderClass} ${
                                                        isDarkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${isFolded ? '-rotate-90' : ''}`}/>
                                                    <span className="flex-1 truncate">
                                                        {journal.startDate ? moment(journal.startDate).format("dddd DD MMMM YYYY") : "—"}
                                                    </span>
                                                    <span className="font-medium normal-case tracking-normal">{dayCount}</span>
                                                </button>
                                            )}
                                            {!isFolded && <button
                                                type="button"
                                                onClick={() => setSelectedJournal(journal)}
                                                aria-current={isActive ? "true" : undefined}
                                                className={`w-full flex items-center gap-3 pl-3 pr-4 py-3 text-left border-l-2 border-b transition-colors ${rowBorderClass} ${
                                                    isActive
                                                        ? "border-l-primary bg-primary/5 dark:bg-primary/10"
                                                        : `border-l-transparent ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"}`
                                                }`}
                                            >
                                                {/* Opening time */}
                                                <div className={`w-12 flex-shrink-0 text-sm font-semibold tabular-nums ${isActive ? "text-primary" : textPrimaryClass}`}>
                                                    {journal.startDate ? moment(journal.startDate).format("HH:mm") : "—"}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm truncate ${isActive ? "text-primary font-medium" : textPrimaryClass}`}>
                                                        {journal.startEmployee?.name || "—"}
                                                    </p>
                                                    {journal.registerName && (
                                                        <p className={`text-xs truncate ${textMutedClass}`}>{journal.registerName}</p>
                                                    )}
                                                </div>
                                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                    <span className={`text-sm font-semibold tabular-nums whitespace-nowrap ${textPrimaryClass}`}>
                                                        {journal.salesAmount ? `${formatPrice(journal.salesAmount)} DH` : "—"}
                                                    </span>
                                                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                                                        isOpen
                                                            ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
                                                            : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                                                    }`}>
                                                        <span className={`h-1.5 w-1.5 rounded-full ${isOpen ? "bg-amber-500 animate-pulse" : "bg-green-500"}`}/>
                                                        {isOpen ? t("journaux.inProgress") : t("journaux.completed")}
                                                    </span>
                                                </div>
                                            </button>}
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                            {/* Main pane */}
                            <div className="flex-1 overflow-y-auto scrollbar-hide" style={{scrollbarWidth:'none'}}>
                                <DetailContent/>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default JournauxTables;
